/**
 * Camada 1c · O CHASSI. Ele MONTA as capacidades transversais, e o app só declara.
 *
 * ---------------------------------------------------------------------------
 * O QUE ELE RESOLVE, e o número foi medido em 05/09/2026
 * ---------------------------------------------------------------------------
 * Para ter Ajuda e Mensagens, cada app repetia **53 linhas** de ligação no próprio `App.tsx`:
 * ler duas variáveis de ambiente pelo nome, montar credenciais num `useMemo`, criar a fonte de
 * ajuda, criar o transporte de conversa, rodar o sino, montar o painel, injetar o item de menu,
 * e atender a rota. Cinco apps declaravam a dependência e **um** ligava.
 *
 * O RESULTADO DISSO estava na tela e ninguém via: em `admin` e `_template`, o botão de Ajuda do
 * cabeçalho existia, era clicável, e **não abria nada**. Sem erro, sem aviso, e sem quebrar o
 * typecheck, porque `painelAjuda` era opcional e a `Topbar` desenhava o `?` sem condição.
 *
 * COM O CHASSI, o app passa a declarar duas coisas no manifesto e a não escrever ligação
 * nenhuma. O que ele não escreve, ele não erra.
 *
 * ---------------------------------------------------------------------------
 * POR QUE AQUI E NÃO NO `AppShell`
 * ---------------------------------------------------------------------------
 * O próprio `AppShell` declara, nele mesmo, que *"o chassi nao conhece o servico de mensagens,
 * e nao deve"*. A decisão está certa e não se revoga: `layout` é apresentação, e apresentação
 * que conhece serviço vira apresentação impossível de testar sem subir o mundo.
 *
 * A COMPOSIÇÃO SOBE UM ANDAR. Este pacote conhece os serviços, o `layout` continua ignorante,
 * e a camada 3 passa a enxergar só um pacote em vez de três.
 */
import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { AppShell, type OpcaoUsuario } from '@complexity/layout';
import { t } from '@complexity/i18n';
import { PainelAjuda, CentralDeAjuda } from '@complexity/help';
import type { ItemMenu, ProductConfig } from '@complexity/product-kit';
import type { Sessao } from '@complexity/auth';
import { ligarArmadilhaDeFoco } from './armadilha-de-foco.js';
import { usarCredenciais } from './credenciais.js';
import { usarFonteDeAjuda } from './fonte-de-ajuda.js';
import { usarMensagens } from './transporte-de-mensagens.js';
import { TelaDeMensagens, ROTA_DE_MENSAGENS } from './tela-de-mensagens.js';

/** A rota que o chassi atende sozinho. O app não a declara e não a trata. */
export const ROTA_DA_AJUDA = '/ajuda';

/**
 * A SEGUNDA ROTA QUE O CHASSI ATENDE, reexportada para o app ter UM lugar de onde importar
 * as duas. O motivo de ela existir esta em `tela-de-mensagens.tsx`: o sino sempre navegou
 * para ca, e ninguem servia, entao a tela nao mudava e o botao parecia morto.
 */
export { ROTA_DE_MENSAGENS };

export interface ChassiProps {
  // ---------------------------------------------------------------------------
  // OBRIGATÓRIO. Sem qualquer um destes, não compila.
  // ---------------------------------------------------------------------------

  /** O manifesto do produto. É dele que saem o namespace da ajuda e o plano da conversa. */
  readonly produto: ProductConfig;

  /**
   * A sessão, e ela aceita `null` DE PROPÓSITO em vez de ser opcional.
   *
   * A DIFERENÇA IMPORTA E NÃO É ESTILO: opcional deixaria um app esquecer de passar e cair no
   * modo sem sessão em silêncio, que é a família de defeito que este pacote existe para
   * fechar. Obrigatória-e-anulável obriga o app a DIZER em qual dos dois casos ele está, e o
   * `_template`, que não tem login, declara `null` explicitamente em vez de inventar uma
   * sessão de fachada.
   */
  readonly sessao: Sessao | null;

  readonly rotaAtiva: string;
  readonly aoNavegar: (rota: string) => void;
  readonly aoSair: () => void;

  /** A área útil do produto. O chassi desenha todo o resto. */
  readonly children: ReactNode;

  // ---------------------------------------------------------------------------
  // PADRÃO. Omitir é escolha válida, e o padrão está escrito ao lado de cada um.
  // ---------------------------------------------------------------------------

  /**
   * Padrão `null`. Vazio não quebra nada: quem decide o cliente de verdade é a sessão no
   * banco, e o cabeçalho é só o que o serviço confere contra ela. No plano `control-plane` ele
   * é nulo SEMPRE, porque o operador atravessa todos os clientes.
   */
  readonly tenantId?: string | null;

  /**
   * Padrão `undefined`, e `undefined` NÃO apaga a faixa: faz aparecer a de ambiente não
   * declarado. É deliberado, e é a mesma escolha da conta de trabalho: ambiente ausente pende
   * para o lado seguro em vez de sumir da tela.
   */
  readonly ambiente?: Parameters<typeof AppShell>[0]['ambiente'];

  /** Padrão: o nome da sessão, depois o e-mail, depois "Visitante" quando não há sessão. */
  readonly nomeUsuario?: string;

  readonly permissoesDoUsuario?: readonly string[];
  readonly opcoesUsuario?: readonly OpcaoUsuario[];

  /**
   * Padrão: nenhuma linha. Os dados da pessoa no painel da conta, quando o produto liga
   * `sairNaConta` no manifesto. Nasceu em 12/09/2026 no Medicina (nome completo e CRM).
   */
  readonly dadosDaConta?: Parameters<typeof AppShell>[0]['dadosDaConta'];
}

/**
 * Monta o app inteiro: menu, cabeçalho, Ajuda, Mensagens e a área do produto.
 */
export function Chassi({
  produto,
  sessao,
  rotaAtiva,
  aoNavegar,
  aoSair,
  children,
  tenantId = null,
  ambiente,
  nomeUsuario,
  permissoesDoUsuario,
  opcoesUsuario,
  dadosDaConta,
}: ChassiProps) {
  /**
   * A ARMADILHA DE FOCO DA GAVETA DO CELULAR, ligada uma vez para o app inteiro.
   *
   * POR QUE ELA E MONTADA AQUI, e nao dentro do `AppShell` que desenha a gaveta: aquele
   * arquivo e o `Rail.tsx` estao CONGELADOS por ordem dele em 06/09/2026, e um hook recusa a
   * escrita nos dois. A armadilha nao precisa daquele estado, e o porque completo esta no
   * cabecalho de `armadilha-de-foco.ts`.
   *
   * O DEFEITO QUE ELA FECHA, medido na suite do VOE: com a gaveta aberta, a 16a parada do Tab
   * caia no botao de tres barras da topbar, que naquele momento esta ATRAS da cortina. Quem
   * navega por teclado alcancava um controle que quem usa o dedo nao alcanca.
   *
   * `useEffect` recebendo a funcao DIRETO, e nao uma seta que a chama: ela ja devolve a
   * propria limpeza, que e a assinatura que o React espera. Lista de dependencias vazia porque
   * ela nao depende de nada do render: quem decide se a armadilha age e o DOM, a cada tecla.
   */
  useEffect(ligarArmadilhaDeFoco, []);

  // AS CREDENCIAIS PRIMEIRO, porque as duas capacidades dependem delas. O `useMemo` de dentro
  // é o que impede a busca em laço, e o motivo está em `credenciais.ts`.
  const credenciais = usarCredenciais(sessao?.token ?? null, tenantId, sessao?.usuario.email ?? null);

  // AS DUAS CAPACIDADES, montadas sempre e nesta ordem. Os ganchos são incondicionais de
  // propósito: chamá-los dentro de um `if` faria a quantidade de hooks mudar entre renders, e
  // o React derruba a árvore inteira. O defeito exato deixou o admin BRANCO em 05/09/2026.
  const ajuda = usarFonteDeAjuda(credenciais, produto.id);
  // O PADRAO TAMBEM PRECISA SER ESTAVEL, e pelo mesmo motivo do `useMemo` em
  // `credenciais.ts`: escrito direto na chamada, este objeto nasceria com
  // identidade nova a cada render e reabriria o laco que acabamos de fechar. O padrao some
  // quando a tranca 1 tornar `mensagens` obrigatorio no manifesto.
  const mensagensDoProduto = useMemo(
    /**
     * O PADRAO SILENCIOSO SAIU EM 06/09/2026, quando `mensagens` virou obrigatorio.
     *
     * ELE ERA A ULTIMA PORTA ABERTA: um app que esquecesse de declarar recebia
     * `{ lado: 'cliente', plano: 'tenant' }` sem nenhum aviso. Num app de plano de controle
     * isso esta errado, e o sintoma nao seria erro: seria o sino somando as proprias
     * respostas do operador como nao lidas, ou 401 sem ninguem entender por que.
     *
     * O `useMemo` FICA, e nao por otimizacao: este objeto e dependencia de efeito do sino,
     * e devolver a referencia do manifesto direto a cada render reabriria o laco de busca
     * que custou uma manha em 06/09/2026.
     */
    () => produto.mensagens,
    [produto.mensagens],
  );
  const sino = usarMensagens(mensagensDoProduto, credenciais);

  /**
   * O ITEM DE MENU DA AJUDA, injetado pelo chassi.
   *
   * POR QUE INJETADO E NÃO DECLARADO PELO APP: item de menu escrito à mão em cada app é a
   * mesma divergência das 53 linhas, só que visível. Com a injeção, todo app ganha o item no
   * mesmo lugar, com o mesmo rótulo, e um app novo não tem como esquecer dele.
   */
  const menu = useMemo((): readonly ItemMenu[] => {
    const itens = produto.menu ?? [];
    // Se o produto já declarou a rota (ha produto que declara), não duplicamos: duplicar
    // desenharia "Ajuda" duas vezes no mesmo menu.
    const jaTem = itens.some((i) => i.rota === ROTA_DA_AJUDA);
    if (jaTem) return itens;

    // OS TRÊS CAMPOS SÃO OBRIGATÓRIOS em `ItemMenu`, e o typecheck recusou a primeira versão
    // deste arquivo por faltarem dois. O `icone` é NOME e não desenho: nome desconhecido faz o
    // rail desenhar um ponto no lugar, e não um espaço vazio, porque item sem nada à esquerda
    // desalinha a lista inteira e lê como defeito de carregamento.
    const itemDaAjuda: ItemMenu = { icone: 'ajuda', rota: ROTA_DA_AJUDA, label: 'Ajuda' };

    // A AJUDA ENTRA ANTES DO PRIMEIRO ITEM MARCADO `depoisDaAjuda`, e no fim quando nenhum é
    // marcado, que é o caso de todo produto que não declara o campo. Pedido do fundador em
    // 12/09/2026 no Medicina: "No menu Sobre vem abaixo de ajuda".
    const primeiroDepois = itens.findIndex((i) => i.depoisDaAjuda);
    if (primeiroDepois < 0) return [...itens, itemDaAjuda];
    return [...itens.slice(0, primeiroDepois), itemDaAjuda, ...itens.slice(primeiroDepois)];
  }, [produto.menu]);

  // O produto que o AppShell recebe leva o menu com a Ajuda dentro. O manifesto original não é
  // mutado: mutar um objeto de configuração compartilhado faz o segundo app receber o menu do
  // primeiro, e o defeito só aparece quando dois apps rodam no mesmo processo.
  const produtoComAjuda = useMemo(() => ({ ...produto, menu }), [produto, menu]);

  /**
   * O ASSUNTO QUE A GAVETA DE AJUDA PEDIU PARA LEVAR À EQUIPE, ou nulo.
   *
   * NASCEU EM 11/09/2026, por decisão do fundador em enquete: o botão "Falar com a nossa equipe" da
   * busca sem resultado existia e nunca aparecia, porque este chassi não passava a função. Ele fecha
   * a gaveta, leva à Central de Ajuda e abre o formulário com o termo buscado.
   */
  const [assuntoParaEquipe, setAssuntoParaEquipe] = useState<string | null>(null);
  // O PEDIDO SE GASTA AO SAIR DA AJUDA: quem volta à Central depois abre a lista, e não o formulário.
  useEffect(() => {
    if (rotaAtiva !== ROTA_DA_AJUDA && assuntoParaEquipe !== null) setAssuntoParaEquipe(null);
  }, [rotaAtiva, assuntoParaEquipe]);

  // O SINO INDISPONÍVEL MOSTRA ZERO, e a marca de "não sei" é da própria Topbar. A diferença
  // entre "não há mensagem" e "não consegui perguntar" é dela, e não daqui.
  const naoLidosDoSino = sino.indisponivel ? 0 : sino.naoLidos;

  /**
   * O NOME FALADO DO SINO, montado aqui quando o produto declara `rotuloDoSino`.
   *
   * SINGULAR E PLURAL SÃO DO CHASSI, e não da Topbar: "1 novas" seria o defeito, e a Topbar é
   * peça congelada do mobile do VOE, que recebe o texto pronto e só o fala. Sem a declaração o
   * valor fica indefinido, e a Topbar continua dizendo "Avisos", como o VOE sempre disse.
   *
   * SEM useMemo DE PROPÓSITO: `t()` depende do idioma escolhido, e um memo preso só à contagem
   * continuaria falando português depois de a pessoa trocar para inglês.
   */
  let rotuloDoSino: string | undefined;
  if (produto.rotuloDoSino) {
    const nome = t(produto.rotuloDoSino);
    const novas = naoLidosDoSino === 1 ? t('nova') : t('novas');
    rotuloDoSino = naoLidosDoSino === 0 ? nome : `${nome}: ${naoLidosDoSino} ${novas}`;
  }

  return (
    <AppShell
      produto={produtoComAjuda}
      ambiente={ambiente}
      rotaAtiva={rotaAtiva}
      aoNavegar={aoNavegar}
      nomeUsuario={nomeUsuario ?? sessao?.usuario.nome ?? sessao?.usuario.email ?? 'Visitante'}
      permissoesDoUsuario={permissoesDoUsuario}
      opcoesUsuario={opcoesUsuario}
      dadosDaConta={dadosDaConta}
      aoSair={aoSair}
      naoLidos={naoLidosDoSino}
      rotuloDoSino={rotuloDoSino}
      // O DESTINO DO SINO DEPENDE DO PRODUTO, e a diferenca e decisao dele em 07/09/2026:
      //
      //   produto de cliente · leva a AJUDA, onde a conversa nasceu e onde ela vive
      //   Super Admin        · leva a MENSAGENS, que e a tela de operacao com as
      //                        conversas de todos os clientes
      //
      // As duas frases dele, no mesmo dia: "QUANDO CLICAR NO SINO DEVERA LEVAR PARA AS
      // MENSAGENS NO ITEM AJUDA DO MENU", e depois "SOMENTE NO SUPER ADMIN, QUANDO CLICAR
      // NO SINO LEVARA PARA A MENSAGENS DO MENU MENSAGENS."
      //
      // A COMPARACAO E POR `produto.id`, que vem do manifesto, e nao por rota nem por
      // porta: manifesto e o que cada app declara sobre si, e e onde uma diferenca de
      // produto deve morar.
      aoAbrirAvisos={() =>
        aoNavegar(produto.id === 'admin' ? ROTA_DE_MENSAGENS : ROTA_DA_AJUDA)
      }
      // A GAVETA DE BUSCA RÁPIDA, que é o que o `?` do cabeçalho abre. Ela deixa de ser
      // opcional na tranca 2, e a partir dali um app sem ela não compila.
      /**
       * A GAVETA RECEBE UMA FUNCAO, e e o `AppShell` quem entrega o fechamento dentro dela.
       *
       * O QUE HAVIA AQUI ERA `aoFechar={() => undefined}`, e ele quebrava a tela: o X da
       * gaveta chamava uma funcao vazia. Relatado por ele em 09/09/2026, "BOTAO X DA AJUDA
       * NAO ESTA FUNCIONANDO", e medido no navegador no mesmo dia: o clique CHEGAVA ao botao
       * (nenhuma interceptacao de ponteiro) e a gaveta continuava desenhada. O Escape falhava
       * junto, porque os dois caminhos passam pela mesma prop.
       *
       * O RACIOCINIO DO COMENTARIO ANTIGO ESTAVA CERTO E A CONCLUSAO ERRADA: o dono do estado
       * e mesmo o `AppShell`, e por isso o painel nao pode fechar por dentro. O que faltava
       * era o dono ENTREGAR o fechamento, e nao o painel receber uma funcao que nao fecha
       * nada. Continua havendo um dono so, e agora ele atende quem pede.
       */
      painelAjuda={(fecharGaveta) => (
        <PainelAjuda
          namespace={produto.ajuda.namespace}
          fonte={ajuda.fonteDaGaveta}
          aoFechar={fecharGaveta}
          aoAbrirConversa={(termo) => {
            fecharGaveta();
            setAssuntoParaEquipe(termo);
            aoNavegar(ROTA_DA_AJUDA);
          }}
        />
      )}
    >
      {/*
        A ROTA DE AJUDA É ATENDIDA AQUI, e não pelo roteador do app.
        POR QUÊ: se cada app atendesse, cada um escolheria um componente, um título e um
        comportamento de rota inexistente. Atendida aqui, a Central é a mesma em todos, e o app
        não precisa saber que ela existe.
      */}
      {rotaAtiva === ROTA_DA_AJUDA && produto.ajuda.tela !== 'propria' ? (
        /*
          A CENTRAL SO ATENDE QUEM NAO DECLAROU AJUDA PROPRIA. O `admin` declara, por
          decisao dele em 07/09/2026: la a Ajuda explica cada item do menu, e nao busca na
          base de conhecimento, porque a base e o material que ELE publica para os clientes.
          Ausente, o campo vale `central`, e todo produto de cliente segue como estava.
        */
        <CentralDeAjuda
          // A `key` REMONTA A CENTRAL quando chega um pedido novo, para o formulário abrir com ele.
          key={assuntoParaEquipe ?? 'central'}
          namespace={produto.ajuda.namespace}
          fonte={ajuda.fonteDaCentral}
          assuntoInicial={assuntoParaEquipe ?? undefined}
        />
      ) : rotaAtiva === ROTA_DE_MENSAGENS && mensagensDoProduto.plano === 'tenant' ? (
        /*
          A ROTA DE MENSAGENS, ATENDIDA AQUI PELO MESMO MOTIVO DA DE AJUDA.
          O `plano === 'tenant'` NAO E FILTRO DE CONVENIENCIA: a tela do operador atravessa
          TODOS os clientes e e do proprio app (apps/admin/src/App.tsx:303). Servir aqui um
          painel de um cliente so trocaria a tela rica dele por uma pobre. O porque completo
          esta no cabecalho de `tela-de-mensagens.tsx`.
        */
        <TelaDeMensagens
          mensagens={mensagensDoProduto}
          credenciais={credenciais}
        />
      ) : (
        children
      )}
    </AppShell>
  );
}
